import { NgStyle } from "@angular/common";
import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { cloneDeep, isEqual, isNumber, sortBy } from "lodash";
import { ComponentChanges } from "../../../models/i-component-change";
import { ChipComponent } from "../../chip/chip.component";
import { DropdownBaseComponent } from "../dropdown-base.component";
import { DropdownOptions, IMultiSelectLayoutOptions } from "../dropdown.models";

@Component({
	selector: "rl-dropdown-multiple",
	templateUrl: "./dropdown-multiple.component.html",
	styleUrls: ["./dropdown-multiple.component.scss"],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DropdownMultipleComponent),
			multi: true
		}
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, FormsModule, NgStyle, ChipComponent]
})
export class DropdownMultipleComponent<T> extends DropdownBaseComponent<T> implements OnInit, OnChanges, ControlValueAccessor {

	@Input()
	options: DropdownOptions<T>;

	@Input()
	showEmptyOption: boolean;

	@Input()
	layoutOptions: IMultiSelectLayoutOptions;

	@Input()
	sortItems: boolean = false;

	@Input()
	dataLabel: string = 'dropdownSingleSelect';

	@Output()
	onValueChange = new EventEmitter<T[]>();

	disabled: boolean;

	public onChange = (val: T[]) => { };
	public onTouched = () => { };
	public onInnerChange: any = () => { };

	public registerOnChange(fn: any): void {
		this.onInnerChange = fn;
	}

	public registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	public setDisabledState?(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	get selectStyleStyle(): {} {
		const style: { height?: string | number } = {};
		if (this.layoutOptions) {
			if (isNumber(this.layoutOptions.height) && this.layoutOptions.height > 0) {
				style.height = this.layoutOptions.height + "px";
			} else {
				style.height = this.layoutOptions.height;
			}
		} else {
			style.height = "auto";
		}
		return style;
	}

	selectedKeys: Set<string | number> = new Set<string | number>();
	get selectedItems() {
		return Array.from(this.selectedKeys).map(key => {
			return this.options.items.find(item => this.options.rowKeyFn(item) === key);
		})
	}

	constructor() {
		super();
	}

	ngOnInit(): void {
		this.writeValue([]);
	}

	ngOnChanges(changes: ComponentChanges<this>): void {
		if (changes.options && !isEqual(changes.options.previousValue, changes.options.currentValue)) {
			this.buildRows();
		}
	}

	writeValue(values: T[]): void {
		if (values) {
			const previousIds = cloneDeep(this.selectedKeys);
			const nextIds = new Set(values.map(x => this.options.rowKeyFn(x)));
			this.selectedKeys = nextIds;
			if (!isEqual(previousIds, nextIds)) {
				this.onInnerChange(values);
			}
			this.onValueChange.emit(values);
		}
	}

	private buildRows() {
		if (!this.options || !this.options.items) {
			this.options.items = [];
			return;
		}

		if (this.sortItems) {
			this.options.items = sortBy(this.options.items, item => this.options.rowLabelFn(item));
		}
	}

	toggleSelected(row: T) {
		let selectedIds: (number | string)[] = Array.from(this.selectedKeys);
		const key = this.options.rowKeyFn(row);
		if (selectedIds.includes(key)) {
			selectedIds = selectedIds.filter(id => id !== key);
		} else {
			selectedIds.push(key);
		}
		const nextValues = this.options.items.filter(x => selectedIds.includes(this.options.rowKeyFn(x))).map(x => x);
		this.writeValue(nextValues);
		this.onValueChange.emit(nextValues);
	}

	toKey(value: unknown) {
		return this.options.rowKeyFn(value);
	}

	handleMouseDown(event: MouseEvent, value: T) {
		event.preventDefault();
		this.toggleSelected(value);
	}

	removeChip(rowKey: string | number) {
		const row = this.options.items.find(x => this.options.rowKeyFn(x) === rowKey);
		this.toggleSelected(row);
	}

	toChipLabel(key: string | number) {
		const found = this.options.items.find(item => this.options.rowKeyFn(item) === key);
		if (!found) {
			return ``;
		}
		return this.options.rowLabelFn(found);
	}

	isSelected(row: T) {
		const selectedKey = this.options.rowKeyFn(row);
		const selected = this.selectedKeys.has(selectedKey);
		if (selected) {
			return true;
		}
		return null; // removes the selected attribute
	}
}
